import { Grade, Language } from "@generated/graphql";
import { Box, Button, Stack, Typography } from "@mui/material";
import palette from "theme/palette";
import { AMOUNT_BUTTONS, GRADE_BUTTONS, LANGUAGE_BUTTONS } from "../utils";
import SectionLabel from "./SectionLabel";

interface SettingsSectionProps {
  grade: Grade;
  amount: string;
  language: Language;
  gradeDescription: string;
  onGradeChange: (grade: Grade) => void;
  onAmountChange: (amount: string) => void;
  onLanguageChange: (language: Language) => void;
  getGradeButtonColors: (
    grade: Grade,
    isActive: boolean
  ) => { borderColor: string; backgroundColor: string; color: string };
}

const SettingsSection = ({
  grade,
  amount,
  language,
  gradeDescription,
  onGradeChange,
  onAmountChange,
  onLanguageChange,
  getGradeButtonColors,
}: SettingsSectionProps) => {
  return (
    <Stack sx={{ height: "100%" }}>
      <SectionLabel>Настройки</SectionLabel>
      <Stack
        spacing={1}
        sx={{
          p: 1.25,
          borderRadius: 1.5,
          border: `1px solid ${palette.divider}`,
          backgroundColor: "bgSwitch",
          height: "100%",
        }}>
        <Box>
          <SectionLabel sx={{ mb: 0.75 }}>Сложность</SectionLabel>
          <Stack
            direction='row'
            spacing={0.5}>
            {GRADE_BUTTONS.map((gradeButton) => {
              const isActive = grade === gradeButton.value;
              const buttonColors = getGradeButtonColors(gradeButton.value, isActive);

              return (
                <Button
                  key={gradeButton.value}
                  variant='outlined'
                  color='secondary'
                  onClick={() => onGradeChange(gradeButton.value)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    px: 1,
                    py: 0.75,
                    borderWidth: 1.5,
                    borderColor: buttonColors.borderColor,
                    backgroundColor: buttonColors.backgroundColor,
                    color: buttonColors.color,
                    fontSize: 14,
                    fontWeight: 700,
                    "&:hover": {
                      borderWidth: 1.5,
                      borderColor: buttonColors.borderColor,
                      backgroundColor: buttonColors.backgroundColor,
                    },
                  }}>
                  {gradeButton.label}
                </Button>
              );
            })}
          </Stack>
          <Typography sx={{ mt: 0.75, fontSize: 14, lineHeight: 1.4, color: "text.secondary" }}>
            {gradeDescription}
          </Typography>
        </Box>

        <Box sx={{ borderTop: `1px solid ${palette.divider}` }} />

        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          spacing={1}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.secondary" }}>Количество вопросов</Typography>
          <Stack
            direction='row'
            spacing={0.5}>
            {AMOUNT_BUTTONS.map((buttonAmount) => {
              const isActive = amount === buttonAmount;

              return (
                <Button
                  key={buttonAmount}
                  color={isActive ? "third" : "secondary"}
                  variant={isActive ? "contained" : "outlined"}
                  size='small'
                  sx={{
                    fontWeight: 500,
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderColor: isActive ? palette.primary.main : palette.border,
                    minWidth: 48,
                  }}
                  onClick={() => onAmountChange(buttonAmount)}>
                  {buttonAmount}
                </Button>
              );
            })}
          </Stack>
        </Stack>

        <Box sx={{ borderTop: `1px solid ${palette.divider}` }} />

        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          spacing={1}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.secondary" }}>Язык</Typography>
          <Stack
            direction='row'
            spacing={0.5}
            useFlexGap
            flexWrap='wrap'
            justifyContent='flex-end'>
            {LANGUAGE_BUTTONS.map((languageButton) => {
              const isActive = language === languageButton.value;

              return (
                <Button
                  key={languageButton.value}
                  color={isActive ? "third" : "secondary"}
                  variant={isActive ? "contained" : "outlined"}
                  size='small'
                  sx={{
                    fontWeight: 500,
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderColor: isActive ? palette.primary.main : palette.border,
                    minWidth: 44,
                  }}
                  onClick={() => onLanguageChange(languageButton.value)}>
                  {languageButton.label}
                </Button>
              );
            })}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SettingsSection;
